export const durationConverter = {
  days: {
    toHours(days: number) {
      return days * 24;
    },
    toMinutes(days: number) {
      return this.toHours(days) * 60;
    },
    toSeconds(days: number) {
      return this.toMinutes(days) * 60;
    },
    toMilliseconds(days: number) {
      return this.toSeconds(days) * 1000;
    },
  },
  hours: {
    toDays(hours: number) {
      return Math.floor(hours / 24);
    },
    toMinutes(hours: number) {
      return hours * 60;
    },
    toSeconds(hours: number) {
      return this.toMinutes(hours) * 60;
    },
    toMilliseconds(hours: number) {
      return this.toSeconds(hours) * 1000;
    },
  },
  minutes: {
    toDays(minutes: number) {
      return Math.floor(this.toHours(minutes) / 24);
    },
    toHours(minutes: number) {
      return Math.floor(minutes / 60);
    },
    toSeconds(minutes: number) {
      return minutes * 60;
    },
    toMilliseconds(minutes: number) {
      return this.toSeconds(minutes) * 1000;
    },
  },
  seconds: {
    toMilliseconds(seconds: number) {
      return seconds * 1000;
    },
    toMinutes(seconds: number) {
      return Math.floor(seconds / 60);
    },
    toHours(seconds: number) {
      return Math.floor(this.toMinutes(seconds) / 60);
    },
    toDays(seconds: number) {
      return Math.floor(this.toHours(seconds) / 24);
    },
    toMinutesAndSeconds(seconds: number) {
      return {
        seconds: seconds % 60,
        minutes: this.toMinutes(seconds),
      };
    },
    toHoursAndMinutes(seconds: number) {
      return {
        minutes: (seconds % 3600) / 60,
        hours: Math.floor(seconds / 3600),
      };
    },
  },
  milliseconds: {
    toSeconds(milliseconds: number) {
      return Math.floor(milliseconds / 1000);
    },
    toMinutes(milliseconds: number) {
      return Math.floor(this.toSeconds(milliseconds) / 60);
    },
    toHours(milliseconds: number) {
      return Math.floor(this.toMinutes(milliseconds) / 60);
    },
    toDays(milliseconds: number) {
      return Math.floor(this.toHours(milliseconds) / 24);
    },
    toHoursMinutesSeconds(milliseconds) {
      let remain = milliseconds ?? 0;

      const hours = durationConverter.milliseconds.toHours(remain);
      remain = remain % (1000 * 60 * 60);

      const minutes = durationConverter.milliseconds.toMinutes(remain);
      remain = remain % (1000 * 60);

      const seconds = durationConverter.milliseconds.toSeconds(remain);
      remain = remain % 1000;

      return { hours, minutes, seconds };
    },
  },
  splitTimeUnits(seconds: number, includeDays = false) {
    const absSeconds = Math.abs(seconds);
    const remainderDays = this.seconds.toDays(absSeconds);
    const daysInSeconds = includeDays ? this.days.toSeconds(remainderDays) : 0;

    const remainderHours = this.seconds.toHours(absSeconds - daysInSeconds);
    const hoursInSeconds = this.hours.toSeconds(remainderHours);

    const remainderMinutes = this.seconds.toMinutes(
      absSeconds - hoursInSeconds - daysInSeconds,
    );
    const minutesInSeconds = this.minutes.toSeconds(remainderMinutes);

    const remainderSeconds =
      absSeconds - hoursInSeconds - minutesInSeconds - daysInSeconds;

    return {
      ...(includeDays ? { days: remainderDays } : {}),
      hours: remainderHours,
      minutes: remainderMinutes,
      seconds: remainderSeconds,
    };
  },
  combineTimeUnits(
    days: number,
    hours: number,
    minutes: number,
    seconds: number,
  ) {
    const daysInSecs = this.days.toSeconds(Math.abs(days));
    const hoursInSecs = this.hours.toSeconds(Math.abs(hours));
    const minutesInSecs = this.minutes.toSeconds(Math.abs(minutes));

    return daysInSecs + hoursInSecs + minutesInSecs + Math.abs(seconds);
  },
  toString(seconds: number) {
    const durations: string[] = [];
    const timeUnits = this.splitTimeUnits(seconds, true);
    if (timeUnits.days && timeUnits.days > 0) {
      durations.push(
        timeUnits.days + (timeUnits.days === 1 ? ' day' : ' days'),
      );
    }
    if (timeUnits.hours > 0) {
      durations.push(
        timeUnits.hours + (timeUnits.hours === 1 ? ' hour' : ' hours'),
      );
    }
    if (timeUnits.minutes > 0) {
      durations.push(
        timeUnits.minutes +
          (timeUnits.minutes === 1 ? '  minute' : '  minutes'),
      );
    }
    if (timeUnits.seconds > 0) {
      durations.push(
        timeUnits.seconds +
          (timeUnits.seconds === 1 ? '  second' : '  seconds'),
      );
    }

    return durations.join(', ');
  },
};

export function isPositiveFiniteNumber(
  maybeNumber: unknown,
): maybeNumber is number {
  return (
    typeof maybeNumber === 'number' &&
    !Number.isNaN(maybeNumber) &&
    Number.isFinite(maybeNumber) &&
    Number.isSafeInteger(maybeNumber) &&
    maybeNumber >= 0
  );
}
